import { useCallback, useEffect, useState } from "react";

interface UseDirtyCloseGuardOptions {
  isDirty: boolean;
  onClose: () => void;
}

interface DirtyCloseGuard {
  openChangeHandler: (next: boolean) => void;
  confirmOpen: boolean;
  confirm: () => void;
  dismiss: () => void;
}

export function useDirtyCloseGuard({
  isDirty,
  onClose,
}: UseDirtyCloseGuardOptions): DirtyCloseGuard {
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Browsers ignore the custom message but show a generic warning.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (ev: BeforeUnloadEvent) => {
      ev.preventDefault();
      ev.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const openChangeHandler = useCallback(
    (next: boolean) => {
      if (next) return;
      if (isDirty) {
        setConfirmOpen(true);
        return;
      }
      onClose();
    },
    [isDirty, onClose],
  );

  const confirm = useCallback(() => {
    setConfirmOpen(false);
    onClose();
  }, [onClose]);

  const dismiss = useCallback(() => setConfirmOpen(false), []);

  return { openChangeHandler, confirmOpen, confirm, dismiss };
}
